import { FC, useState, useRef } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import {
  Box,
  Cell,
  CustomModalLayout,
  FormField,
  Layout,
  Modal,
  Checkbox,
  Input,
  Text,
} from 'wix-style-react';
import { classes } from './NotificationSettingsModal.st.css';
import { useAppSelector, useAppDispatch } from 'src/hooks';
import { accountSelector } from 'src/redesign/features/Account/store/selectors';
import { updateAccount, getAccountInfo } from 'src/redesign/features/Account/store/actions';
import { showNotification } from '../../Toast/store/actions';
import StatusAlertSmall from 'wix-ui-icons-common/StatusAlertSmall';
import CircleSmallFilled from 'wix-ui-icons-common/CircleSmallFilled';
import { Button } from 'src/redesign/components/Button';
import {
  InvisibleRecaptcha,
  InvisibleRecaptchaHandle,
} from 'src/redesign/components/InvisibleRecaptcha';
import { EmailVerification } from './components/EmailVerification';

type NotificationSettings = {
  notification_email?: string;
  new_order_confirmation: boolean;
  new_event_request_submitted: boolean;
  product_updates: boolean;
  transfer_requests: boolean;
  cancel_requests: boolean;
  refund_requests: boolean;
  contact_host: boolean;
};

type NotificationSettingsModalProps = {
  isVisible?: boolean;
  setVisible: (value: boolean) => void;
};

export const NotificationSettingsModal: FC<NotificationSettingsModalProps> = ({
  isVisible,
  setVisible,
}) => {
  const [loading, setLoading] = useState(false);
  const account = useAppSelector(accountSelector);
  const dispatch = useAppDispatch();
  const [showVerification, setShowVerification] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string>('');
  const [pendingSettings, setPendingSettings] = useState<any>(null);
  const recaptchaRef = useRef<InvisibleRecaptchaHandle>(null);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);

  const { handleSubmit, control, watch } = useForm<NotificationSettings>({
    defaultValues: {
      notification_email: account?.notification_email ?? account?.email ?? '',
      new_order_confirmation: account?.notification_settings?.new_order_confirmation ?? true,
      new_event_request_submitted:
        account?.notification_settings?.new_event_request_submitted ?? true,
      product_updates: account?.notification_settings?.product_updates ?? true,
      transfer_requests: account?.notification_settings?.transfer_requests ?? true,
      cancel_requests: account?.notification_settings?.cancel_requests ?? true,
      refund_requests: account?.notification_settings?.refund_requests ?? true,
      contact_host: account?.notification_settings?.contact_host ?? true,
    },
  });

  const notificationEmail = watch('notification_email');
  const emailHasChanged = notificationEmail !== account?.notification_email;
  const isEmailVerified = account?.notification_email_verified_at && !emailHasChanged;
  const hasNotificationEmail = !!notificationEmail;
  const showUnverifiedWarning = hasNotificationEmail && !isEmailVerified;

  const closeModal = () => {
    setVisible(false);
    setShowVerification(false);
    setPendingEmail('');
    setPendingSettings(null);
  };

  const handleVerifyEmailClick = () => {
    setPendingEmail(notificationEmail);
    setPendingSettings(null); // Don't save settings, just verify
    setShowVerification(true);
  };

  const onSubmit: SubmitHandler<NotificationSettings> = async (data) => {
    const { notification_email, ...notificationSettings } = data;

    // Check if email changed and needs verification
    if (emailHasChanged && notification_email) {
      setPendingEmail(notification_email);
      setPendingSettings(notificationSettings);
      setShowVerification(true);
      return;
    }

    // If no email change or email is already verified, save directly
    await saveSettings(notification_email, notificationSettings);
  };

  const saveSettings = async (notification_email: string, notificationSettings: any) => {
    setLoading(true);

    try {
      await dispatch(
        updateAccount({
          notification_email,
          notification_settings: notificationSettings,
        }),
      ).unwrap();

      dispatch(
        showNotification({
          severity: 'success',
          summary: 'Notification Settings',
          detail: 'Settings updated successfully',
          life: 3000,
        }),
      );

      closeModal();
    } catch (error) {
      console.error(error);
      dispatch(
        showNotification({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update notification settings',
          life: 3000,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationComplete = async () => {
    try {
      // If we have pending settings, save them along with verification
      if (pendingSettings) {
        await saveSettings(pendingEmail, pendingSettings);
      } else {
        // Just verified without saving settings, refresh account and show success
        dispatch(
          showNotification({
            severity: 'success',
            summary: 'Email Verified',
            detail: 'Your notification email has been verified successfully.',
            life: 3000,
          }),
        );

        // Reload account to get updated verification status
        await dispatch(getAccountInfo()).unwrap();

        setShowVerification(false);
        setPendingEmail('');
      }
    } catch (error: any) {
      console.error('Error after verification:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to complete process. Please try again.';
      dispatch(
        showNotification({
          severity: 'error',
          summary: 'Error',
          detail: errorMessage,
          life: 5000,
        }),
      );
    }
  };

  const handleVerificationCancel = () => {
    setShowVerification(false);
    setPendingEmail('');
    setPendingSettings(null);
  };

  const renderForm = () => (
    <Layout>
      <Cell span={12}>
        <Box direction="vertical" gap={3}>
          <div className={classes.heading}>Email Notifications</div>
          <div className={classes.subtext}>
            Choose which notifications you'd like to receive via email.
          </div>

          {/* Email Address Field */}
          <Controller
            control={control}
            name="notification_email"
            rules={{
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address',
              },
            }}
            render={({ field, fieldState }) => (
              <FormField
                label="Notification Email Address"
                required
                infoContent="Enter the email address where you'd like to receive notifications. If left empty, we'll use your account email."
              >
                <Box direction="vertical" gap={2}>
                  <Input
                    {...field}
                    status={fieldState.error ? 'error' : undefined}
                    statusMessage={fieldState.error?.message}
                    placeholder={account?.email || 'Enter email address'}
                    type="email"
                  />

                  {/* Verification Status */}
                  {isEmailVerified && (
                    <Box gap={1} align="left">
                      <CircleSmallFilled
                        style={{ color: '#22c55e', width: '16px', height: '16px' }}
                      />
                      <Text size="small" weight="normal" secondary>
                        Email verified
                      </Text>
                    </Box>
                  )}

                  {showUnverifiedWarning && (
                    <Box direction="vertical" gap={2}>
                      <Box gap={1} align="center">
                        <StatusAlertSmall
                          style={{ color: '#f59e0b', width: '16px', height: '16px' }}
                        />
                        <Text size="small" weight="normal" secondary>
                          {emailHasChanged
                            ? 'Email changed - verification required'
                            : 'Email not verified - click below to verify'}
                        </Text>
                      </Box>
                      {!emailHasChanged && (
                        <Button size="medium" priority="primary" onClick={handleVerifyEmailClick}>
                          Verify Email
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
              </FormField>
            )}
          />

          <Box direction="vertical" gap={2}>
            {/* Order & Sales Notifications */}
            <Controller
              control={control}
              name="new_order_confirmation"
              render={({ field }) => (
                <FormField>
                  <Checkbox
                    {...field}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  >
                    New Order Confirmation
                  </Checkbox>
                </FormField>
              )}
            />

            {/* Event Management Notifications */}
            <Controller
              control={control}
              name="new_event_request_submitted"
              render={({ field }) => (
                <FormField>
                  <Checkbox
                    {...field}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  >
                    New Event Request Submitted
                  </Checkbox>
                </FormField>
              )}
            />

            {/* Customer Service Requests */}
            <Controller
              control={control}
              name="transfer_requests"
              render={({ field }) => (
                <FormField>
                  <Checkbox
                    {...field}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  >
                    Transfer Requests
                  </Checkbox>
                </FormField>
              )}
            />

            <Controller
              control={control}
              name="cancel_requests"
              render={({ field }) => (
                <FormField>
                  <Checkbox
                    {...field}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  >
                    Cancel Requests
                  </Checkbox>
                </FormField>
              )}
            />

            <Controller
              control={control}
              name="refund_requests"
              render={({ field }) => (
                <FormField>
                  <Checkbox
                    {...field}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  >
                    Refund Requests
                  </Checkbox>
                </FormField>
              )}
            />

            <Controller
              control={control}
              name="contact_host"
              render={({ field }) => (
                <FormField>
                  <Checkbox
                    {...field}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  >
                    Contact Messages from Customers
                  </Checkbox>
                </FormField>
              )}
            />

            {/* Product Updates */}
            <Controller
              control={control}
              name="product_updates"
              render={({ field }) => (
                <FormField>
                  <Checkbox
                    {...field}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  >
                    Product Updates
                  </Checkbox>
                </FormField>
              )}
            />
          </Box>
        </Box>
      </Cell>
    </Layout>
  );

  const renderModalContent = () => (
    <CustomModalLayout
      primaryButtonText="Save Settings"
      primaryButtonProps={{ disabled: loading }}
      secondaryButtonText="Cancel"
      onCloseButtonClick={closeModal}
      primaryButtonOnClick={handleSubmit(onSubmit)}
      secondaryButtonOnClick={closeModal}
      title="Notification Settings"
      width="500px"
      height="700px"
    >
      {renderForm()}
    </CustomModalLayout>
  );

  return (
    <>
      {!showVerification && (
        <Modal isOpen={isVisible} onRequestClose={closeModal} screen="desktop">
          {renderModalContent()}
        </Modal>
      )}

      {showVerification && (
        <Modal isOpen={isVisible} onRequestClose={closeModal} screen="desktop">
          <CustomModalLayout
            title="Verify Email Address"
            onCloseButtonClick={closeModal}
            width="500px"
            height="auto"
          >
            <Layout>
              <Cell span={12}>
                <Box direction="vertical" gap={3} paddingTop={2} paddingBottom={2}>
                  <div className={classes.subtext}>
                    We need to verify your notification email address to ensure you receive
                    important notifications.
                  </div>

                  <EmailVerification
                    email={pendingEmail}
                    onVerified={handleVerificationComplete}
                    onCancel={handleVerificationCancel}
                    invisibleRecaptchaRef={recaptchaRef}
                    recaptchaLoaded={recaptchaLoaded}
                  />

                  {/* Invisible ReCAPTCHA */}
                  <InvisibleRecaptcha
                    ref={recaptchaRef}
                    onLoad={() => setRecaptchaLoaded(true)}
                    onVerify={(token) => {
                      console.log('reCAPTCHA verified:', token);
                    }}
                    onExpired={() => {
                      console.log('reCAPTCHA expired');
                      setRecaptchaLoaded(false);
                    }}
                    onError={() => {
                      console.error('reCAPTCHA error');
                    }}
                  />
                </Box>
              </Cell>
            </Layout>
          </CustomModalLayout>
        </Modal>
      )}
    </>
  );
};
